import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  getCatalogoExamesControllerObterQueryKey,
  useCatalogoExamesControllerExcluirReferencia,
  useCatalogoExamesControllerObter,
} from '@/api/generated/admin-catalogo-exames/admin-catalogo-exames';
import type { ReferenciaResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { GradeDeFaixas } from '@/features/admin/catalogo-exames/components/GradeDeFaixas';
import { ExameFormDialogo } from '@/features/admin/catalogo-exames/components/ExameFormDialogo';
import { ReferenciaFormDialogo } from '@/features/admin/catalogo-exames/components/ReferenciaFormDialogo';
import { descreverFaixa, descreverPublico } from '@/features/admin/catalogo-exames/rotulos';
import { EstadoCarregando, EstadoErro } from '@/shared/components/estados';
import { ROTAS } from '@/shared/lib/rotas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Separator } from '@/shared/ui/separator';
import { toast } from '@/shared/ui/sonner';

interface FaixaEmFoco {
  analitoId: string;
  referencia?: ReferenciaResponse;
}

/**
 * Exame do catálogo: analitos e faixas de referência (F6-3).
 *
 * Os três níveis vivem na mesma tela de propósito — uma faixa só faz sentido
 * junto do analito, e um analito só junto do exame. Navegar entre três telas
 * para cadastrar uma faixa seria trabalho sem informação nova.
 */
export function ExameDetalhePage() {
  const { id = '' } = useParams<{ id: string }>();
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [exameEmEdicao, setExameEmEdicao] = useState(false);
  const [faixaEmFoco, setFaixaEmFoco] = useState<FaixaEmFoco | null>(null);
  const [aExcluir, setAExcluir] = useState<{ analitoId: string; faixa: ReferenciaResponse } | null>(
    null,
  );

  const { data: exame, isPending, isError, refetch } = useCatalogoExamesControllerObter(id);

  const excluirReferencia = useCatalogoExamesControllerExcluirReferencia();

  const podeEditar = pode('admin_catalogo_exames.editar');

  async function recarregar() {
    await queryClient.invalidateQueries({ queryKey: getCatalogoExamesControllerObterQueryKey(id) });
  }

  async function confirmarExclusaoDaFaixa() {
    if (!aExcluir) return;
    try {
      await excluirReferencia.mutateAsync({
        id,
        analitoId: aExcluir.analitoId,
        referenciaId: aExcluir.faixa.id,
      });
      await recarregar();
      toast.success('Faixa excluída.');
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível excluir a faixa.');
    } finally {
      setAExcluir(null);
    }
  }

  if (isPending) return <EstadoCarregando rotulo="Carregando exame..." />;
  if (isError || !exame) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <Link
        to={ROTAS.adminCatalogoExames}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-brand transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar ao portfólio
      </Link>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 p-6 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-semibold text-foreground">{exame.nome}</h2>
              {!exame.ativo && (
                <Badge variant="outline" className="text-destructive border-destructive/40">
                  Desativado
                </Badge>
              )}
              {exame.categoria && <Badge variant="outline">{exame.categoria}</Badge>}
            </div>
            {exame.descricao && (
              <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{exame.descricao}</p>
            )}
          </div>

          {podeEditar && (
            <Button variant="outline" onClick={() => setExameEmEdicao(true)}>
              <Pencil className="w-4 h-4" />
              Editar exame
            </Button>
          )}
        </div>

        <Separator />

        <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 text-sm">
          <div>
            <dt className="text-muted-foreground">Método analítico</dt>
            <dd className="text-foreground">{exame.metodoAnalitico ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Código do laboratório</dt>
            <dd className="text-foreground">{exame.codigoLaboratorio ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Código do Vertis</dt>
            <dd className="text-foreground">{exame.codigoVertis ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Analitos</dt>
            <dd className="text-foreground">{exame.totalAnalitos}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Exames solicitados</dt>
            <dd className="text-foreground">{exame.totalExames}</dd>
          </div>
        </dl>
      </div>

      <GradeDeFaixas
        exame={exame}
        podeEditar={podeEditar}
        aoAbrirDetalheDaFaixa={(analitoId, faixa) => {
          const analito = exame.analitos.find((a) => a.id === analitoId);
          const referencia = faixa ? analito?.referencias.find((r) => r.id === faixa.id) : undefined;
          setFaixaEmFoco({ analitoId, ...(referencia ? { referencia } : {}) });
        }}
      />

      <ExameFormDialogo
        aberto={exameEmEdicao}
        aoFechar={() => setExameEmEdicao(false)}
        exame={exame}
      />

      {faixaEmFoco && (
        <ReferenciaFormDialogo
          aberto
          aoFechar={() => setFaixaEmFoco(null)}
          exameId={id}
          analitoId={faixaEmFoco.analitoId}
          {...(faixaEmFoco.referencia ? { referencia: faixaEmFoco.referencia } : {})}
        />
      )}

      <AlertDialog open={Boolean(aExcluir)} onOpenChange={(v) => !v && setAExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta faixa?</AlertDialogTitle>
            <AlertDialogDescription>
              {aExcluir && (
                <>
                  {descreverPublico(aExcluir.faixa)} — {descreverFaixa(aExcluir.faixa)}. Laudos
                  emitidos a partir de agora deixam de comparar o resultado com ela.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmarExclusaoDaFaixa()}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
